<template>
  <div>
    <v-row v-if="!fileIsProcessing">
      <v-col
        cols="lg-6"
        offsetLg="3">
        <div class="d-sm-flex justify-stretch gap-1 mb-6">
          <v-text-field
            ref="trackingNumberInput"
            :value="trackingNumber"
            :label="$vuetify.lang.t('$vuetify.labels.trackingNumber')"
            outlined
            hideDetails
            clearable
            @input="matchOrders($event)" />
          <div class="d-flex justify-center justify-sm-stretch gap-1 mb-6 mt-6 mt-sm-0">
            <QrCodeReaderComponent
              height="56"
              @decode="matchOrders($event)" />
            <v-btn
              height="auto"
              color="success"
              @click="showResult">
              <v-icon v-if="!isUploading">
                $vuetify.icons.check
              </v-icon>
              <v-progress-circular
                v-else
                indeterminate
                color="white" />
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- RESULT OF SHIPMENT PAIRING -->
    <v-row v-if="result && !isLoading">
      <v-col
        cols="lg-6"
        offsetLg="3">
        <h3>
          {{ $vuetify.lang.t('$vuetify.shipments.reported') }} {{ data.length }} {{ $vuetify.lang.t('$vuetify.shipments.shipments') }},
          {{ $vuetify.lang.t('$vuetify.shipments.scanned') }} {{ result.pairedFilesCount }} {{ $vuetify.lang.t('$vuetify.shipments.shipments') }},
          {{ $vuetify.lang.t('$vuetify.shipments.missingShipments') }}:
        </h3>
        <v-list lines="one">
          <v-list-item
            v-for="file in result.notPairedFileNames"
            :key="file">
            {{ file }}
          </v-list-item>
        </v-list>
      </v-col>
    </v-row>

    <!-- SHIPMENT TABLE -->
    <v-row>
      <v-col
        cols="lg-6"
        offsetLg="3">
        <v-data-table
          ref="shipmentsTable"
          :headers="shipmentsTableHeaders"
          :items="data"
          :itemClass="itemRowBackground"
          :loading="isLoading"
          :itemsPerPage="15"
          :elevation="0" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import QrCodeReaderComponent from "@/components/global/QrCodeReaderComponent.vue";
import { EcoChallengeFileApiService } from "@/common/services/eco-challenge-file.api.service";
import {mapActions, mapGetters} from "vuex";

export default {
  name: "ShipmentScanTableComponent",

  components: {QrCodeReaderComponent},

  props: {
    /**
     * @type {Array<EcoChallengeFileOrdersModel>}
     */
    data: {
      type: Array,
      required: true,
    },
    fileId: {
      type: Number,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      shipmentsTableHeaders: [
        {
          text: this.$vuetify.lang.t('$vuetify.labels.trackingNumber'),
          align: 'left',
          sortable: true,
          value: 'parsedCode',
        },
        {
          text: this.$vuetify.lang.t('$vuetify.labels.isPaired'),
          align: 'left',
          sortable: true,
          value: 'isPaired',
        },
      ],
      isUploading: false,
      /**
       * An array of files to be uploaded.
       * @type {Array<File>}
       */
      filesToUpload: [],
      // Initialize an object to track intervals for each file
      fileIntervals: {},
      // Initialize an object to track the status of each file
      fileStatuses: {},
      fileIsProcessing: false,
      qrCodeReaderShow: false,
      trackingNumber: '',
      /**
       * @type {Array<AttachmentModel>}
       */
      attachments: [],
      result: null,
    }
  },

  computed: {
    ...mapGetters({ checkedFiles: "files/getCheckedFiles" }),
  },

  watch: {
    'isLoading': {
      handler(val) {
        if (!val && this.checkedFiles.includes(this.fileId)) {
          this.showResult();
        }
      }
    }
  },

  mounted() {
    if (this.checkedFiles.includes(this.fileId)) {
      this.showResult();
    }

    this.$refs.trackingNumberInput.focus();
  },

  methods: {
    ...mapActions({ setCheckedFile: "files/setCheckedFile" }),

    /**
     * Match orders based on tracking number.
     * @param {string} result - The tracking number to match orders for.
     */
    async matchOrders(result) {
      this.trackingNumber = result;

      try {
        const foundShipmentIndex = this.data.findIndex(shipment => shipment.parsedCode === result);

        if (foundShipmentIndex !== -1) {
          const fileId = this.data[foundShipmentIndex].ecoChallengeFileId;
          const shipment = (await EcoChallengeFileApiService.createOrdersPairing(fileId, { loaded_code: result })).getItem();

          this.$set(this.data, foundShipmentIndex, shipment);
        }
      } catch (error) {
        this._handleError(error);
      }
    },

    /**
     * Set the background color of table rows.
     * @param {object} item - The item for which to set the background color.
     * @returns {string|null} - The background color or null.
     */
    itemRowBackground(item) {
      if (item.isPaired) {
        return "bg-deep-purple-lighten-4";
      }
    },

    /**
     * Show the result of pairing.
     */
    showResult() {
      this.$refs.trackingNumberInput.blur();
      this.setCheckedFile(this.fileId);
      const notPairedFiles = this.data.filter(shipment => !shipment.isPaired);

      this.result = {
        notPairedFileNames: notPairedFiles.map(shipment => shipment.parsedCode),
        pairedFilesCount: this.data.length - notPairedFiles.length,
      }
    },

    _handleError(error) {
      if (error.response.status === 429) {
        this.$alertError(this.$vuetify.lang.t('$vuetify.messages.APICallsNumberExceeded'));
      } else if (error.response.data.errors) {
        this.$alertError(error.response.data.errors[0].message);
      } else {
        this.$alertError(error.response.data.message);
      }
    },
  }
}
</script>

<style>
.bg-deep-purple-lighten-4 {
  background-color: #d1c4e9;
}
</style>

